import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import { updateOrderStatus } from "../../../services/Admin/OrdersApi";
import SendViaWhatsApp from "./SendViaWhatsApp";

// map status -> { label (Arabic), bg, text }
const STATUS_MAP = {
  pending: { label: "قيد الانتظار", bg: "#FFC107", text: "#000" },
  processing: { label: "قيد المعالجة", bg: "#1976d2", text: "#fff" },
  confirmed: { label: "مؤكّد", bg: "#0288d1", text: "#fff" },
  shipped: { label: "تم الشحن", bg: "#7E57C2", text: "#fff" },
  delivered: { label: "تم التوصيل", bg: "#2E7D32", text: "#fff" },
  canceled: { label: "ملغى", bg: "#D32F2F", text: "#fff" },
};
const STATUSES = Object.keys(STATUS_MAP);

const EditOrderStatusDialog = ({ open, onClose, order, onSuccess }) => {
  const [status, setStatus] = useState(order?.status || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // new states for whatsapp flow
  const [confirmWhatsAppOpen, setConfirmWhatsAppOpen] = useState(false);
  const [sendWhatsAppOpen, setSendWhatsAppOpen] = useState(false);
  const [pendingSaveAfterWhatsApp, setPendingSaveAfterWhatsApp] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus(order?.status || "");
      setError("");
      setConfirmWhatsAppOpen(false);
      setSendWhatsAppOpen(false);
      setPendingSaveAfterWhatsApp(false);
    }
  }, [open, order]);

  const performSave = async () => {
    if (!order?.id) return;
    setError("");
    setLoading(true);
    try {
      const res = await updateOrderStatus(order.id, status);
      if (res?.success) {
        onSuccess?.();
      } else {
        setError(res?.message || "فشل تحديث حالة الطلب");
      }
    } catch (err) {
      setError(err?.message || "حدث خطأ أثناء التحديث");
    } finally {
      setLoading(false);
      // cleanup
      setPendingSaveAfterWhatsApp(false);
    }
  };

  const handleSave = () => {
    // ask user if they want to send WhatsApp before saving
    setConfirmWhatsAppOpen(true);
  };

  const handleConfirmChoice = (sendWhatsApp) => {
    setConfirmWhatsAppOpen(false);
    if (sendWhatsApp) {
      // open send dialog, then perform save after send completes (onSent)
      setPendingSaveAfterWhatsApp(true);
      setSendWhatsAppOpen(true);
    } else {
      // just save
      performSave();
    }
  };

  const handleWhatsAppSent = (sent) => {
    // close send dialog and proceed to save regardless (sent==true means send attempted)
    setSendWhatsAppOpen(false);
    if (pendingSaveAfterWhatsApp) {
      performSave();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>تعديل حالة الطلب</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* عرض بيانات مختصرة للطلب والعميل */}
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">رقم الطلب</Typography>
            <Typography variant="body2">{order?.order_number ?? order?.id ?? "-"}</Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">العميل</Typography>
            <Typography variant="body2">{order?.client?.name || order?.client?.full_name || order?.client?.email || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">{order?.client?.phone || "-"}</Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">العنوان</Typography>
            <Typography variant="body2">{order?.address || order?.checkout?.address || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {order?.city ? `${order?.city} — ${order?.governorate || "-"}` : (order?.checkout ? `${order.checkout.city_id ? (order.checkout.city_id) : "-" } — ${order.checkout.governorate_id ?? "-"}` : "-")}
            </Typography>
            <Typography variant="caption" color="text.secondary">هاتف: {order?.phone || order?.checkout?.phone || "-"}</Typography>
          </Stack>

          <FormControl fullWidth size="small">
            <InputLabel>الحالة</InputLabel>
            <Select value={status} label="الحالة" onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">اختر الحالة</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: STATUS_MAP[s].bg, borderRadius: 0 }} />
                    <Typography variant="body2">{STATUS_MAP[s].label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>إلغاء</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || !status}>
          {loading ? <CircularProgress size={20} /> : "حفظ"}
        </Button>
      </DialogActions>

      {/* confirmation: send via WhatsApp? */}
      <Dialog open={confirmWhatsAppOpen} onClose={() => setConfirmWhatsAppOpen(false)}>
        <DialogTitle>إرسال إشعار عبر واتساب</DialogTitle>
        <DialogContent>
          <Typography>هل تريد إرسال تحديث حالة الطلب إلى العميل عبر واتساب قبل الحفظ؟</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmChoice(false)}>لا - فقط حفظ</Button>
          <Button onClick={() => handleConfirmChoice(true)} variant="contained">نعم - إرسال عبر واتساب</Button>
        </DialogActions>
      </Dialog>

      {/* send via WhatsApp dialog */}
      <SendViaWhatsApp
        open={sendWhatsAppOpen}
        onClose={() => handleWhatsAppSent(false)}
        onSent={() => handleWhatsAppSent(true)}
        order={order}
        client={order?.client}
        orderNumber={order?.order_number}
        country={order?.country} // new: pass country name
      />
    </Dialog>
  );
};

export default EditOrderStatusDialog;
